// src/hooks/useTimeSeriesApi.tsx

import { API } from 'aws-amplify'
import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  ReactNode,
} from 'react'

import { SensorId } from '@/types/domain'

import { authHeader, notifyError, normalizedSensorType } from '../api/'
import { usePreloader } from '../components/'

import { refreshToken } from './auth/'

const API_URL = () => import.meta.env.REACT_APP_API_URL

const TimeSeriesCtx = createContext({ data: { key: null, items: {} } })

const __lastFeelingCache = new Map<string, Record<string, any>>()

const makeRangeKey = (fData, from, to) => {
  const id =
    fData?.entity_id ??
    fData?.entity_id ??
    fData?.attributes?.entity_id ??
    fData?.attributes?.entity_id ??
    'x'
  return `${id}|${from}|${to}`
}

type Props = {
  children: ReactNode
  folderData: any
  fromDate: string
  toDate: string
}

export const TimeSeriesDataProvider = ({
  children,
  folderData,
  fromDate,
  toDate,
}: Props) => {
  const { getByFeelingTypes } = useTimeSeriesApi()
  const [data, setData] = useState({ key: null, items: {} })

  useEffect(() => {
    let active = true

    const run = async () => {
      if (!folderData || !fromDate || !toDate) return

      const key = makeRangeKey(folderData, fromDate, toDate)

      try {
        const result = await getByFeelingTypes(folderData, fromDate, toDate)
        if (!active) return

        setData({
          key,
          items: result?.items || {},
        })
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        const msg =
          error?.message ||
          (typeof error === 'string' ? error : 'Unbekannter Fehler')
        notifyError(
          `time series pool error${status ? ` (${status})` : ''}: ${msg}`
        )
      }
    }

    run()

    return () => {
      active = false
    }
  }, [getByFeelingTypes, folderData, fromDate, toDate])

  return (
    <TimeSeriesCtx.Provider value={{ data }}>{children}</TimeSeriesCtx.Provider>
  )
}

export const useTimeSeriesApi = () => {
  const { start, stop } = usePreloader()
  const pool = useContext(TimeSeriesCtx)

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          e2.status = e2?.status ?? e2?.response?.status ?? 401
          throw e2
        }
      }

      throw {
        message: error?.message || String(error),
        status,
      }
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getTimeSeries = useCallback(
    (id: SensorId, fromDate, toDate, granularity = null) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id || id <= 0) return null

          const from = `${fromDate} 00:00:00`
          const to = `${toDate} 23:59:59`

          let query =
            `from=${encodeURIComponent(from)}` + `&to=${encodeURIComponent(to)}`
          if (granularity) query += `&period=${encodeURIComponent(granularity)}`

          const base = API_URL()
          const url = `${base}/sensors/admin/history/${id}?${query}`
          const options = {
            headers: { ...(authHeader() || {}) },
            method: 'GET',
          }
          const response = await fetch(url, options)

          if (!response.ok) {
            const text = await response.text().catch(() => '')
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          const payload = await response.json()

          return payload?.data?.attributes?.time_series ?? null
        })
      ),
    [withRequest, withPreloader]
  )

  const getByFeelingTypes = useCallback(
    (folderData, fromDate, toDate) =>
      withPreloader(() =>
        withRequest(async () => {
          const rangeKey = makeRangeKey(folderData, fromDate, toDate)

          const hasContextCache =
            pool?.data?.key === rangeKey &&
            pool?.data?.items &&
            Object.keys(pool.data.items).length > 0

          if (hasContextCache) return { items: pool.data.items }

          if (__lastFeelingCache.has(rangeKey))
            return { items: __lastFeelingCache.get(rangeKey) }

          const sensors = Array.isArray(folderData?.sensors)
            ? folderData.sensors
            : []

          const allowed = new Set(['temperature', 'co2', 'light', 'humidity'])
          const filtered = sensors.filter(s => {
            const raw = String(s?.sensor_type ?? '')
              .toLowerCase()
              .trim()
            const norm = String(
              normalizedSensorType(s?.sensor_type ?? '') ?? ''
            )
              .toLowerCase()
              .trim()
            return allowed.has(raw) || allowed.has(norm)
          })

          if (filtered.length === 0) {
            __lastFeelingCache.set(rangeKey, {})
            return { items: {} }
          }

          const options = {
            method: 'GET',
            headers: { ...(authHeader() || {}) },
          }

          const from = `${fromDate} 00:00:00`
          const to = `${toDate} 23:59:59`

          const items = {}

          await Promise.all(
            filtered.map(async s => {
              const id = s?.entity_id
              if (!id) return

              const endpoint = `/sensors/admin/history/${id}?from=${encodeURIComponent(
                from
              )}&to=${encodeURIComponent(to)}`

              const res = await API.get('karmaApi', endpoint, options)
              const ts = res?.data?.attributes?.time_series
              if (ts) items[id] = ts
            })
          )

          __lastFeelingCache.set(rangeKey, items)
          return { items }
        })
      ),
    [withRequest, pool, withPreloader]
  )

  const getDataListReload = useCallback(
    (timeSeriesDataList, fromDate, toDate) =>
      withPreloader(() =>
        withRequest(async () => {
          const ids = Object.keys(timeSeriesDataList?.items || {})
          if (ids.length === 0) return { items: {} }

          const options = {
            method: 'GET',
            headers: { ...(authHeader() || {}) },
          }
          const from = `${fromDate} 00:00:00`
          const to = `${toDate} 23:59:59`

          const items = {}
          await Promise.all(
            ids.map(async id => {
              const endpoint = `/sensors/admin/history/${id}?from=${encodeURIComponent(
                from
              )}&to=${encodeURIComponent(to)}`
              try {
                const response = await API.get('karmaApi', endpoint, options)
                const series = response?.data?.attributes?.time_series
                if (series) items[id] = series
              } catch (error) {
                const status = error?.status ?? error?.response?.status
                const msg =
                  error?.message ||
                  (typeof error === 'string'
                    ? error
                    : `Reload error for sensor ${id}`)
                notifyError(`${msg}${status ? ` (${status})` : ''}`)
              }
            })
          )
          return { items }
        })
      ),
    [withRequest, withPreloader]
  )

  return {
    getTimeSeries,
    getByFeelingTypes,
    getDataListReload,
  }
}
